import React from 'react';
import { getVerificationTier } from '../../utils/xp';

// Helper function to check if user is official TopBlast account
const isOfficialAccount = (username) => {
  if (!username) return false;
  const lowerUsername = username.toLowerCase();
  return lowerUsername === 'topblast' || 
         lowerUsername === 'official topblast' || 
         lowerUsername === 'topblast official' ||
         lowerUsername === 'topblastofficial' ||
         lowerUsername === 'dex' || // Legacy support
         lowerUsername === 'official dex' || 
         lowerUsername === 'dex official' ||
         lowerUsername === 'dexofficial';
};

// TopBlast ranking checkmark component - shows exact rank badges
const VerifiedBadge = ({ 
  username, 
  userXP = 0, 
  showTierName = false, 
  size = "sm",
  overrideTier = null 
}) => {
  const isOfficial = isOfficialAccount(username);
  const tier = overrideTier || getVerificationTier(userXP);
  
  // Size variants
  const sizes = {
    xs: "w-3 h-3 text-xs",
    sm: "w-4 h-4 text-sm", 
    md: "w-5 h-5 text-base",
    lg: "w-6 h-6 text-lg"
  };
  
  const sizeClass = sizes[size] || sizes.sm;
  
  // Official TopBlast account gets special blue checkmark
  if (isOfficial) {
    return (
      <div className={`inline-flex items-center ${showTierName ? 'ml-1' : ''}`}>
        <svg className={`${sizeClass} text-[#1D9BF0]`} fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" fill="#1D9BF0"/>
          <path d="M9.29 16.29L5.7 12.7c-.39-.39-.39-1.02 0-1.41.39-.39 1.02-.39 1.41 0L10 14.17l6.88-6.88c.39-.39 1.02-.39 1.41 0 .39.39.39 1.02 0 1.41l-7.59 7.59c-.38.39-1.02.39-1.41 0z" fill="white"/>
        </svg>
        {showTierName && <span className="ml-1 text-xs text-blue-400 font-medium">TopBlast</span>}
      </div>
    );
  }
  
  // TopBlast rank-based checkmarks (Rank 1-5 only, Rank 0 gets no checkmark)
  if (tier && tier.id !== 'paperhands' && tier.checkmark) {
    // Special handling for brown checkmark (Fartmark rank)
    if (tier.id === 'fartmark') {
      return (
        <div className={`inline-flex items-center ${showTierName ? 'ml-1' : ''}`}>
          <svg className={`${sizeClass} text-[#8B4513]`} fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" fill="#8B4513"/>
            <path d="M9.29 16.29L5.7 12.7c-.39-.39-.39-1.02 0-1.41.39-.39 1.02-.39 1.41 0L10 14.17l6.88-6.88c.39-.39 1.02-.39 1.41 0 .39.39.39 1.02 0 1.41l-7.59 7.59c-.38.39-1.02.39-1.41 0z" fill="white"/>
          </svg>
          {showTierName && <span className="ml-1 text-xs font-medium" style={{ color: tier.color }}>Rank {tier.rank}</span>}
        </div>
      );
    }
    
    // Special handling for gold checkmark with metallic gradient
    if (tier.id === 'gold') {
      return (
        <div className={`inline-flex items-center ${showTierName ? 'ml-1' : ''}`}>
          <svg className={`${sizeClass}`} fill="currentColor" viewBox="0 0 24 24">
            <defs>
              <linearGradient id="goldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#FFD700" />
                <stop offset="20%" stopColor="#FFC125" />
                <stop offset="40%" stopColor="#FFB90F" />
                <stop offset="60%" stopColor="#CDAD00" />
                <stop offset="80%" stopColor="#DAA520" />
                <stop offset="100%" stopColor="#B8860B" />
              </linearGradient>
              <filter id="goldShine">
                <feGaussianBlur stdDeviation="1" result="coloredBlur"/>
                <feMerge>
                  <feMergeNode in="coloredBlur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
            </defs>
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" fill="url(#goldGradient)" filter="url(#goldShine)"/>
            <path d="M9.29 16.29L5.7 12.7c-.39-.39-.39-1.02 0-1.41.39-.39 1.02-.39 1.41 0L10 14.17l6.88-6.88c.39-.39 1.02-.39 1.41 0 .39.39.39 1.02 0 1.41l-7.59 7.59c-.38.39-1.02.39-1.41 0z" fill="white"/>
          </svg>
          {showTierName && <span className="ml-1 text-xs font-medium text-yellow-500">Rank {tier.rank}</span>}
        </div>
      );
    }
    
    // Special handling for diamond checkmark with crystal sparkle effect
    if (tier.id === 'diamond') {
      return (
        <div className={`inline-flex items-center ${showTierName ? 'ml-1' : ''}`}>
          <svg className={`${sizeClass}`} fill="currentColor" viewBox="0 0 24 24">
            <defs>
              <radialGradient id="diamondGradient" cx="30%" cy="30%" r="70%">
                <stop offset="0%" stopColor="#E0F7FF" />
                <stop offset="20%" stopColor="#B9F2FF" />
                <stop offset="40%" stopColor="#7DD3FC" />
                <stop offset="60%" stopColor="#38BDF8" />
                <stop offset="80%" stopColor="#0EA5E9" />
                <stop offset="100%" stopColor="#0284C7" />
              </radialGradient>
              <filter id="diamondSparkle">
                <feGaussianBlur stdDeviation="0.5" result="coloredBlur"/>
                <feMerge>
                  <feMergeNode in="coloredBlur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
            </defs>
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" fill="url(#diamondGradient)" filter="url(#diamondSparkle)"/>
            <path d="M9.29 16.29L5.7 12.7c-.39-.39-.39-1.02 0-1.41.39-.39 1.02-.39 1.41 0L10 14.17l6.88-6.88c.39-.39 1.02-.39 1.41 0 .39.39.39 1.02 0 1.41l-7.59 7.59c-.38.39-1.02.39-1.41 0z" fill="white"/>
            {/* Sparkle effects */}
            <circle cx="8" cy="8" r="0.5" fill="white" opacity="0.8">
              <animate attributeName="opacity" values="0.8;0.3;0.8" dur="2s" repeatCount="indefinite"/>
            </circle>
            <circle cx="16" cy="7" r="0.3" fill="white" opacity="0.6">
              <animate attributeName="opacity" values="0.6;0.2;0.6" dur="1.5s" repeatCount="indefinite"/>
            </circle>
            <circle cx="7" cy="16" r="0.4" fill="white" opacity="0.7">
              <animate attributeName="opacity" values="0.7;0.1;0.7" dur="1.8s" repeatCount="indefinite"/>
            </circle>
          </svg>
          {showTierName && <span className="ml-1 text-xs font-medium text-cyan-400">Rank {tier.rank}</span>}
        </div>
      );
    }
    
    // Special handling for obsidian checkmark with volcanic glass effect
    if (tier.id === 'obsidian') {
      return (
        <div className={`inline-flex items-center ${showTierName ? 'ml-1' : ''}`}>
          <svg className={`${sizeClass}`} fill="currentColor" viewBox="0 0 24 24">
            <defs>
              <radialGradient id="obsidianGradient" cx="20%" cy="20%" r="80%">
                <stop offset="0%" stopColor="#4B5563" />
                <stop offset="30%" stopColor="#374151" />
                <stop offset="60%" stopColor="#1F2937" />
                <stop offset="80%" stopColor="#111827" />
                <stop offset="100%" stopColor="#000000" />
              </radialGradient>
              <filter id="obsidianGlow">
                <feGaussianBlur stdDeviation="1.5" result="coloredBlur"/>
                <feMerge>
                  <feMergeNode in="coloredBlur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
            </defs>
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" fill="url(#obsidianGradient)" filter="url(#obsidianGlow)" stroke="#6B7280" strokeWidth="0.5"/>
            <path d="M9.29 16.29L5.7 12.7c-.39-.39-.39-1.02 0-1.41.39-.39 1.02-.39 1.41 0L10 14.17l6.88-6.88c.39-.39 1.02-.39 1.41 0 .39.39.39 1.02 0 1.41l-7.59 7.59c-.38.39-1.02.39-1.41 0z" fill="#D1D5DB"/>
            {/* Volcanic glass reflections */}
            <path d="M6 6 Q12 4 18 6 Q16 8 12 7 Q8 8 6 6" fill="#6B7280" opacity="0.3"/>
            <path d="M7 15 Q10 14 13 15 Q11 16 9 16 Q8 16 7 15" fill="#9CA3AF" opacity="0.2"/>
          </svg>
          {showTierName && <span className="ml-1 text-xs font-medium text-gray-400">Rank {tier.rank}</span>}
        </div>
      );
    }
    
    // Other rank checkmarks (fallback)
    return (
      <div className={`inline-flex items-center ${showTierName ? 'ml-1' : ''}`}>
        <div 
          className={`${sizeClass} flex items-center justify-center rounded-full border-2 shadow-sm`}
          style={{ 
            borderColor: tier.color,
            backgroundColor: `${tier.color}15`,
            transition: 'all 0.2s ease' // Tesla-like smooth transitions
          }}
          title={`${tier.name} (Rank ${tier.rank}) - ${tier.description}`}
        >
          <span className="text-xs filter drop-shadow-sm" style={{ color: tier.color }}>
            {tier.checkmark}
          </span>
        </div>
        {showTierName && (
          <span 
            className="ml-1 text-xs font-medium" 
            style={{ color: tier.color }}
          >
            Rank {tier.rank}
          </span>
        )}
      </div>
    );
  }
  
  // Rank 0 (Paper Hands) gets no checkmark
  return null;
};

export default VerifiedBadge;